import { API_MAPPING } from '../api-utils/api-url-map';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import SessionStorage from '../SessionStorage';
import { HmacMD5, enc } from 'crypto-js';
import { dynamicRequest, DynamicResponse, RequestOptions } from '../api-utils/api-utils';
import { isIOS } from '../cob-utils';
import { setServerTimeDiff } from '../acquisition-utils';

export interface IAcquisitionRequestOptions {
    body?: Record<string, any>;
    method?: 'GET' | 'get' | 'POST' | 'post';
    appendToUrl?: string;
}

export type AcquisitionInitResponse = {
    PQMaintenanceMode: boolean;
    PAMaintenanceMode: boolean;
    SPMaintenanceMode: boolean;
    Token: string;
    SessionId: string;
};

const acquisitionsConfigs = PathUtils.getMetaPropertyValue('AcquisitionsConfigurations') || '{}';
const acquisitionsConfigsJson = JSON.parse(acquisitionsConfigs);

export const acquisitionApiRequest = async <Type>(name: keyof typeof API_MAPPING, options: IAcquisitionRequestOptions, providerInitResponse?:any): Promise<DynamicResponse<Type>> => {
    if (name === 'init-compare-api') {
        if (!SessionStorage.verifyNotEmpty('C1B-Serv-Time-Diff')) {
            await setServerTimeDiff();
        }
    }

    const requestType = options.method || API_MAPPING[name]?.method || 'GET';
    const body = requestType.toLowerCase() === 'post' && options.body ? options.body : undefined;
    const params = options.appendToUrl || undefined;
    const url = API_MAPPING[name].endpoint;

    const headers = getAcquisitionHeaders(providerInitResponse);
    headers['X-C1B-SIGNATURE'] = buildRequestSignature(url, body, requestType, headers, params);

    const requestOptions: RequestOptions = {
        requestType,
        body,
        headers,
    };
    const skipPa = SessionStorage.tryGet('skipPa', false);
    if (skipPa && isIOS()) {
        console.error(`Headers: ${JSON.stringify(headers)}`);
    }
    return dynamicRequest<Type>(name, requestOptions, params);
};

const getAcquisitionHeaders = (providerInitResponse?:any): Record<string, any> => {
    const serverClientTimeDiff = parseInt(SessionStorage.tryGet('C1B-Serv-Time-Diff', '0') as string);
    const headers = {
        'X-C1B-CLIENTID': acquisitionsConfigsJson.clientId,
        'X-C1B-TIMESTAMP': new Date().getTime() + serverClientTimeDiff,
        'X-C1B-SID': getInitResponse(providerInitResponse)?.SessionId ? getInitResponse(providerInitResponse).SessionId : {},
    };
    return headers;
};

const getInitResponse = (providerInitResponse?:any) => {
    const skipPa = SessionStorage.tryGet('skipPa', false);
    if (skipPa && isIOS()) {
        if (SessionStorage.verifyNotEmpty('initResponse')) {
            console.error('getInitResponse in AcquisitionAPI util: ' + sessionStorage.getItem('initResponse'));
        } else {
            console.error(Object.keys(sessionStorage));
        }
    }
    //In the case where the initResponse is present but the sessionID is wiped
    //Sets session storage with the data that was in the provider 
    if (providerInitResponse && (!SessionStorage.verifyNotEmpty('initResponse')  || !SessionStorage.getObject('initResponse')?.SessionId)) {
        console.error('initResponse not in session storage but in the provider');
        SessionStorage.setObject('initResponse', providerInitResponse);
        if (skipPa) {
            const params = new URLSearchParams(window.location.search);
            params.set('errorCode', 'processingError');
            window.location.assign(window.location.origin + '?' + params.toString());
        }
        return providerInitResponse;
    }   
    return SessionStorage.tryGetObject('initResponse', {});
};

const buildRequestSignature = (url: string, body: Record<string, any> | undefined, method: string, headers: Record<string, string>, params?: string): string => {
    let requestUrl = url;
    const clientId = headers['X-C1B-CLIENTID'];
    const sessionId = headers['X-C1B-SID'];
    const timeStamp = headers['X-C1B-TIMESTAMP'];
    const stringBody = body? JSON.stringify(body) : '';

    if (!requestUrl.startsWith('http')) {
        requestUrl = document.location.protocol + '//' + document.location.host + requestUrl;
    }
    if (params) {
        requestUrl += params;
    }

    const canonicalString = method.toUpperCase() + requestUrl.toLowerCase() + 'X-C1B-CLIENTID:' +  clientId + '&X-C1B-SID:' + sessionId + '&X-C1B-TIMESTAMP:' + timeStamp + stringBody;
    const { Token } = getInitResponse();

    if (Token) {
        const hash = HmacMD5(canonicalString, Token);
        return hash.toString(enc.Base64);
    }

    return '';
};